/**
 * Global keyboard shortcuts (ADR-0010/D-5). Shortcuts map to **commands**; screens register handlers for the
 * commands they support, and each registration carries the same permission as its button (PLATFORM-KI-028: a
 * shortcut must never bypass a permission). Browser-reserved keys (Ctrl+N/T/W) are not used.
 */

export const shortcutCommands = ["save", "new", "post", "print", "search", "help", "manual", "menuSearch"] as const;
export type ShortcutCommand = (typeof shortcutCommands)[number];

/** Command → key combinations, e.g. `Ctrl+Shift+P`. Configurable per tenant/user later (settings module). */
export type ShortcutMap = Record<ShortcutCommand, readonly string[]>;

export const defaultShortcutMap: ShortcutMap = {
  save: ["Ctrl+S"],
  new: ["Ctrl+Alt+N"],
  post: ["Ctrl+Shift+P"],
  print: ["Ctrl+P"],
  search: ["Ctrl+F", "F3"],
  help: ["Ctrl+/"],
  /** Context help: the user manual's page of the current screen (D16). */
  manual: ["F1"],
  /** Focuses the side navigation search box. */
  menuSearch: ["Ctrl+K"],
};

export interface KeyCombo {
  key: string;
  ctrl: boolean;
  alt: boolean;
  shift: boolean;
}

const modifierNames = new Set(["ctrl", "control", "cmd", "meta", "mod", "alt", "option", "shift"]);

/** Parses `Ctrl+Shift+P` (case-insensitive). `Ctrl` also matches ⌘ on macOS. */
export function parseShortcut(text: string): KeyCombo {
  const parts = text.split("+").map((part) => part.trim());
  // "Ctrl++" means the plus key.
  if (text.endsWith("++")) parts.splice(-2, 2, "+");
  const key = parts.at(-1);
  if (!key || modifierNames.has(key.toLowerCase())) {
    throw new RangeError(`Shortcut "${text}" has no key`);
  }
  const modifiers = new Set(parts.slice(0, -1).map((part) => part.toLowerCase()));
  for (const modifier of modifiers) {
    if (!modifierNames.has(modifier)) throw new RangeError(`Unknown modifier "${modifier}" in "${text}"`);
  }
  return {
    key: key.length === 1 ? key.toLowerCase() : key,
    ctrl: ["ctrl", "control", "cmd", "meta", "mod"].some((name) => modifiers.has(name)),
    alt: modifiers.has("alt") || modifiers.has("option"),
    shift: modifiers.has("shift"),
  };
}

const punctuationCodes: Record<string, string> = {
  "/": "Slash",
  ".": "Period",
  ",": "Comma",
  ";": "Semicolon",
  "-": "Minus",
  "=": "Equal",
  "[": "BracketLeft",
  "]": "BracketRight",
};

/** Physical key code for a printable key, so shortcuts work with the Arabic keyboard layout active. */
function codeFor(key: string): string | undefined {
  if (/^[a-z]$/u.test(key)) return `Key${key.toUpperCase()}`;
  if (/^[0-9]$/u.test(key)) return `Digit${key}`;
  return punctuationCodes[key];
}

export interface KeyEventLike {
  key: string;
  code: string;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
}

export function matchesShortcut(event: KeyEventLike, combo: KeyCombo): boolean {
  if ((event.ctrlKey || event.metaKey) !== combo.ctrl || event.altKey !== combo.alt) return false;
  // Shift is implied for some punctuation on some layouts (e.g. "/" on AZERTY); only enforce it for letters/keys.
  const code = codeFor(combo.key);
  const shiftMatters = combo.shift || code === undefined || /^Key|^Digit/u.test(code);
  if (shiftMatters && event.shiftKey !== combo.shift) return false;
  if (code !== undefined && event.code === code) return true;
  const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
  return key === combo.key;
}

/** Merges overrides into the default map. */
export function resolveShortcutMap(overrides: Partial<ShortcutMap> = {}): ShortcutMap {
  return { ...defaultShortcutMap, ...overrides };
}

/** The command bound to an event, if any. */
export function commandFor(event: KeyEventLike, map: ShortcutMap): ShortcutCommand | undefined {
  return shortcutCommands.find((command) => map[command].some((text) => matchesShortcut(event, parseShortcut(text))));
}
